import {defineStore} from 'pinia';
import {ProjectDocumentApi} from '@/api/project-document.apis';
import {ProjectDocumentNs} from '@shared/types';
import {useSelectedProjectStore} from '@domains/boards/stores/selected-project.store';
import {useWorkspaceStore} from '@shared/stores/workspace.store';
import {toast} from '@shared/ms-components'

const PAGE_SIZE = 30;

interface ProjectDocumentState {
  documents: ProjectDocumentNs.ProjectDocument[];
  hasMore: boolean;
  beforeCursor: string | null;
  isLoading: boolean;

  detailDocument: ProjectDocumentNs.ProjectDocumentDetail | null;
  isDetailLoading: boolean;

  isDeleting: boolean;
  isUploading: boolean;
}

export const useProjectDocumentStore = defineStore('projectDocument', {
  state: (): ProjectDocumentState => ({
    documents: [],
    hasMore: false,
    beforeCursor: null,
    isLoading: false,
    detailDocument: null,
    isDetailLoading: false,
    isDeleting: false,
    isUploading: false,
  }),

  getters: {
    getDocuments: (state) => state.documents,
    getDetail: (state) => state.detailDocument,
    canLoadMore: (state) => state.hasMore && !state.isLoading,
  },

  actions: {
    reset(): void {
      this.documents = [];
      this.hasMore = false;
      this.beforeCursor = null;
      this.detailDocument = null;
    },

    /** Replace the list with the first page (cursor reset). */
    async fetchList(): Promise<void> {
      const ws = useWorkspaceStore().getSelectedWorkspaceId;
      const proj = useSelectedProjectStore().getSelectedProjectId;
      if (!ws || ws === '-1' || !proj) return;

      this.isLoading = true;
      try {
        const page = await ProjectDocumentApi.list({
          workspaceId: ws,
          projectId: proj,
          before: null,
          limit: PAGE_SIZE,
        });
        this.documents = Array.isArray(page.rows) ? page.rows : [];
        this.hasMore = !!page.hasMore;
        this.beforeCursor = page.before ?? null;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchMore(): Promise<void> {
      if (!this.canLoadMore || !this.beforeCursor) return;
      const ws = useWorkspaceStore().getSelectedWorkspaceId;
      const proj = useSelectedProjectStore().getSelectedProjectId;
      if (!ws || ws === '-1' || !proj) return;

      this.isLoading = true;
      try {
        const page = await ProjectDocumentApi.list({
          workspaceId: ws,
          projectId: proj,
          before: this.beforeCursor,
          limit: PAGE_SIZE,
        });
        const incoming = Array.isArray(page.rows) ? page.rows : [];
        this.documents = [...this.documents, ...incoming];
        this.hasMore = !!page.hasMore;
        this.beforeCursor = page.before ?? null;
      } finally {
        this.isLoading = false;
      }
    },

    async fetchDetail(documentKey: string): Promise<void> {
      const ws = useWorkspaceStore().getSelectedWorkspaceId;
      const proj = useSelectedProjectStore().getSelectedProjectId;
      if (!ws || ws === '-1' || !proj) return;

      this.detailDocument = null;
      this.isDetailLoading = true;
      try {
        this.detailDocument = await ProjectDocumentApi.detail({
          workspaceId: ws,
          projectId: proj,
          documentKey,
        });
      } catch (err: unknown) {
        const status = (err as {response?: {status?: number}})?.response?.status;
        if (status === 404) {
          toast.error('Document not found, please refresh the page');
        }
        throw err;
      } finally {
        this.isDetailLoading = false;
      }
    },

    closeDetail(): void {
      this.detailDocument = null;
    },

    /** Adds files to the board itself — see ProjectDocumentApi.upload. */
    async uploadDocuments(
      files: File[],
      source?: ProjectDocumentNs.DocumentSource,
    ): Promise<ProjectDocumentNs.ProjectDocument[]> {
      const ws = useWorkspaceStore().getSelectedWorkspaceId;
      const proj = useSelectedProjectStore().getSelectedProjectId;
      if (!ws || ws === '-1' || !proj || !files.length) return [];

      this.isUploading = true;
      try {
        const answer = await ProjectDocumentApi.upload(
          {
            workspaceId: ws,
            projectId: proj,
            origin: source?.origin,
            originUrl: source?.url,
            originRef: source?.ref,
          },
          files,
        );
        const rows = Array.isArray(answer?.rows) ? answer.rows : [];
        // Newest first, and the batch keeps the order the files were given in.
        this.documents = [...[...rows].reverse(), ...this.documents];
        return rows;
      } finally {
        this.isUploading = false;
      }
    },

    async renameDocument(documentKey: string, title: string): Promise<void> {
      const ws = useWorkspaceStore().getSelectedWorkspaceId;
      const proj = useSelectedProjectStore().getSelectedProjectId;
      if (!ws || ws === '-1' || !proj) return;

      const saved = await ProjectDocumentApi.rename({
        workspaceId: ws,
        projectId: proj,
        documentKey,
        title,
      });

      this.documents = this.documents.map(d =>
        d.documentKey === documentKey ? { ...d, title: saved.title } : d,
      );
      if (this.detailDocument?.documentKey === documentKey) {
        this.detailDocument = { ...this.detailDocument, title: saved.title };
      }
    },

    /** A source's own words, fetched without opening it. */
    async contentsOf(documentKey: string): Promise<string | null> {
      if (this.detailDocument?.documentKey === documentKey) {
        return this.detailDocument.fileContent ?? null;
      }

      const ws = useWorkspaceStore().getSelectedWorkspaceId;
      const proj = useSelectedProjectStore().getSelectedProjectId;
      if (!ws || ws === '-1' || !proj) return null;

      const doc = await ProjectDocumentApi.detail({
        workspaceId: ws,
        projectId: proj,
        documentKey,
      });
      return doc.fileContent ?? null;
    },

    async deleteDocument(documentKey: string): Promise<void> {
      const ws = useWorkspaceStore().getSelectedWorkspaceId;
      const proj = useSelectedProjectStore().getSelectedProjectId;
      if (!ws || ws === '-1' || !proj) return;

      this.isDeleting = true;
      try {
        await ProjectDocumentApi.delete({
          workspaceId: ws,
          projectId: proj,
          documentKey,
        });
        this.documents = this.documents.filter(d => d.documentKey !== documentKey);
        if (this.detailDocument?.documentKey === documentKey) {
          this.detailDocument = null;
        }
        toast.success('Document deleted');
      } finally {
        this.isDeleting = false;
      }
    },
  },
});
